import * as React from "react";
import DocumentTitle from "../misc/components/DocumentTitle";
import { LocalizedDateTime, DateTimeAccuracy } from "../misc/components/LocalizedDateTime";
import { AuditTrailEvent } from "../misc/api/apiTypes";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { formatNull } from "../misc/components/Formatters/Formatters";
import { EventModificationDetails } from "./EventModificationDetails";
import { Link, useParams } from "react-router-dom";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import { useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";

interface ExpectedMatchParams {
    id: string;
}

export const AuditTrailIndividualViewScreen = () => {
    const { id } = useParams();

    const [auditEvent, setAuditEvent] = React.useState<AuditTrailEvent>(null);
    const [failedToLoad, setFailedToLoad] = React.useState<boolean>(false);
    const [legacyApi] = React.useState(useWebPortalLegacyClient());

    React.useEffect(
        () => {
            const fetchEvent = async () => {
                try {
                    const event = await legacyApi.getAuditTrailEvent(id);
                    setAuditEvent(event);
                    setFailedToLoad(false);
                } catch {
                    setFailedToLoad(true);
                }
            };

            fetchEvent();
        },
        [id]
    );

    if (failedToLoad) {
        return (
            <Alert show={true} category={AlertCategoryEnum.Danger}>
                Failed to load the Audit Trail Event
            </Alert>
        );
    }

    if (auditEvent === null) {
        return <LoadingWithMessage message="Loading Audit Trail Event..." />;
    }

    return (
        <DocumentTitle title="Audit Trail Event | Vitalograph Web Portal">
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Audit Trail Event</h2>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-heading">Details</div>
                        <div className="panel-body">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Occurred</th>
                                        <th>User</th>
                                        <th>IP Address</th>
                                        <th>User Agent</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <LocalizedDateTime
                                                time={auditEvent.EventDateTime}
                                                accuracy={DateTimeAccuracy.Second}
                                                isTooltip={true}
                                            />
                                        </td>
                                        <td>
                                            {formatNull(
                                                auditEvent.User ? auditEvent.User.Username : null
                                            )}
                                        </td>
                                        <td>
                                            {formatNull(
                                                auditEvent.SignIn
                                                    ? auditEvent.SignIn.IPAddress
                                                    : null
                                            )}
                                        </td>
                                        <td>
                                            {formatNull(
                                                auditEvent.SignIn
                                                    ? auditEvent.SignIn.UserAgent
                                                    : null
                                            )}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <table className="table">
                                <colgroup>
                                    <col style={{ width: `${50}%` }} />
                                    <col style={{ width: `${50}%` }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Summary</th>
                                        <th>Annotation</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{auditEvent.SummaryText}</td>
                                        <td>{auditEvent.UserAnnotation}</td>
                                    </tr>
                                </tbody>
                            </table>

                            {auditEvent.AdditionalDetailsText ? <table className="table">
                                <colgroup>
                                    <col style={{ width: `${100}%` }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Additional Log Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{auditEvent.AdditionalDetailsText}</td>
                                    </tr>
                                </tbody>
                            </table> : null}
                            

                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Audit Trail Action</th>
                                        <th>Study Id</th>
                                        <th>Site Id</th>
                                        <th>Subject Id</th>
                                        <th>Visit Id</th>
                                        <th>Session Id </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{auditEvent.ActionDescription}</td>
                                        <td>{auditEvent.StudyId}</td>
                                        <td>{auditEvent.SiteId}</td>
                                        <td>{auditEvent.SubjectId}</td>
                                        <td>{auditEvent.VisitId}</td>
                                        <td>{auditEvent.SessionId}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <EventModificationDetails entities={auditEvent.Entities} />
                </div>
            </div>
            <div className="col-md-12">
                <div className="h2 pull-right">
                    <Link className="btn btn-primary" to="/Audit">
                        Return to List
                    </Link>
                </div>
            </div>
        </DocumentTitle>
    );
};
